/**
 * Draft Mode Context
 * 
 * Provides draft mode state and actions to the component tree.
 * Wraps the draftModeStore with React Context for easier consumption.
 */

import React, { createContext, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import isEqual from 'lodash.isequal';
import {
  useDraftModeStore,
  selectIsDraftMode,
  selectDraftStatus,
  selectLastSavedTime,
  selectSaveError,
  selectIsPublishing,
  selectIsReadOnly,
} from '@/stores/draftModeStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useSessionHistoryStore } from '@/stores/sessionHistoryStore';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useAutosave } from '@/hooks/useAutosave';
import { useDraftSave } from '@/hooks/useDraftSave';
import { useDraftDiscardActions } from '@/hooks/useDraftDiscardActions';
import { useGuardedModeSwitch } from '@/hooks/useGuardedModeSwitch';
import { useSessionAutoCapture } from '@/hooks/useSessionAutoCapture';
import type { DraftStatus } from '@/types/draftMode';

// =============================================================================
// TYPES
// =============================================================================

export interface DraftModeContextValue {
  // State
  /** Whether the designer is in draft mode (editable) */
  isDraftMode: boolean;
  /** Whether the designer is read-only (not in draft mode) */
  isReadOnly: boolean;
  /** Whether production and draft artifacts are identical */
  areProdAndDraftEqual: boolean;
  /** Current draft status */
  draftStatus: DraftStatus;
  
  // Autosave state
  /** Whether an autosave is in progress */
  isAutoSaving: boolean;
  /** Last autosave timestamp */
  lastSavedTime: Date | null;
  /** Last autosave error message */
  saveError: string | null;
  /** Whether there are unsaved changes */
  needsSaved: boolean;
  
  // Publishing state
  /** Whether a publish operation is in progress */
  isPublishing: boolean;

  // Actions
  /** Switch to draft mode (editable) */
  switchToDraft: () => void;
  /** Switch to published mode (read-only) */
  switchToPublished: () => void;
  /** Discard draft changes and reset to production */
  discardDraft: () => void;
  /** Discard unsaved changes (revert to last saved draft) */
  discardSession: () => void;
  /** Save draft immediately (for running draft). Returns true if save succeeded. */
  saveDraftNow: () => Promise<boolean>;
}

// =============================================================================
// CONTEXT
// =============================================================================

const DraftModeContext = createContext<DraftModeContextValue | null>(null);

// =============================================================================
// PROVIDER PROPS
// =============================================================================

export interface DraftModeProviderProps {
  /** Workflow name for autosave */
  workflowName: string;
  /** Whether in monitoring view (disables editing) */
  isMonitoringView?: boolean;
  /** Children components */
  children: React.ReactNode;
}

// =============================================================================
// PROVIDER
// =============================================================================

export const DraftModeProvider: React.FC<DraftModeProviderProps> = ({
  workflowName,
  isMonitoringView = false,
  children,
}) => {
  const { canEdit: hasEditPermission } = useAppPermission();
  
  // Get state from store
  const isDraftMode = useDraftModeStore(selectIsDraftMode);
  const draftStatus = useDraftModeStore(selectDraftStatus);
  const lastSavedTimeFromStore = useDraftModeStore(selectLastSavedTime);
  const saveErrorFromStore = useDraftModeStore(selectSaveError);
  const isPublishing = useDraftModeStore(selectIsPublishing);
  const isReadOnlyFromStore = useDraftModeStore(selectIsReadOnly);

  // Get workflow store state (for prod/draft equality + dirty checks)
  const workflow = useWorkflowStore((state) => state.workflow);
  const connections = useWorkflowStore((state) => state.connections);
  const isDirty = useWorkflowStore((state) => state.isDirty);

  // Mirror draft/published mode into the workflow store so position writers
  // and the layout-store branch readers target the correct branch.
  useEffect(() => {
    useWorkflowStore.setState({ currentMode: isDraftMode ? 'draft' : 'published' });
  }, [isDraftMode]);

  // Get production artifacts from draft mode store for comparison
  const prodWorkflow = useDraftModeStore((state) => state.prodWorkflow);
  const prodConnections = useDraftModeStore((state) => state.prodConnections);


  // Track if session has been initialized for current workflow
  const sessionInitializedForWorkflowRef = useRef<string | null>(null);

  // Initialize session history when workflow loads
  useEffect(() => {
    if (workflow && connections !== undefined) {
      const { initSession } = useSessionHistoryStore.getState();
      const { draftParameters, draftNotes } = useDraftModeStore.getState();

      // Only init session if we haven't initialized for this specific workflow yet
      // This prevents re-initialization if history is cleared (e.g., via clearSession)
      if (sessionInitializedForWorkflowRef.current !== workflowName) {
        initSession(
          {
            workflow,
            connections,
            parameters: draftParameters,
            notes: draftNotes,
          },
          'Session start'
        );
        sessionInitializedForWorkflowRef.current = workflowName;
      }
    }
  }, [workflow, connections, workflowName]);

  // Enable auto-capture for session history
  useSessionAutoCapture();

  // Compute read-only state: not in draft mode, monitoring view, or lacking edit permission
  const isReadOnly = isReadOnlyFromStore || isMonitoringView || !hasEditPermission;
  
  // Compute prod/draft equality by comparing current workflow with production
  // This uses the actual workflowStore state (what user is editing) vs stored production
  const areProdAndDraftEqual = useMemo(() => {
    if (!workflow || !prodWorkflow) return true;
    // Use isDirty as a quick check - if dirty, definitely not equal
    if (isDirty) return false;
    // Deep compare current workflow with production
    return isEqual(workflow, prodWorkflow) && isEqual(connections, prodConnections);
  }, [workflow, prodWorkflow, connections, prodConnections, isDirty]);
  
  // Use autosave hook. The autosave-mode preference (off/30s/5min/instant)
  // is read directly from `useUIPreferencesStore` inside the hook — we only
  // pass external overrides here (read-only / monitoring gates).
  const {
    isAutoSaving,
    lastSavedTime: lastSavedTimeFromAutosave,
    saveError: saveErrorFromAutosave,
    needsSaved,
  } = useAutosave({
    workflowName,
    // The off/30s/5min/instant preference is read inside the hook.
    enabled: isDraftMode && !isMonitoringView,
    isMonitoringView,
  });
  
  // Use draft save hook for manual/immediate saves (e.g., before running draft)
  const { save: saveDraft } = useDraftSave({ workflowName });
  
  // Prefer autosave state, fall back to store state
  const lastSavedTime = lastSavedTimeFromAutosave ?? lastSavedTimeFromStore;
  const saveError = saveErrorFromAutosave ?? saveErrorFromStore;
  
  // Actions
  // Mode switching goes through the shared guard hook so unsaved draft
  // edits can't be silently dropped when flipping into the read-only
  // published view. The hook is also used by `WorkflowOverflowMenu` so
  // both entry points present the same confirmation flow.
  const {
    requestSwitchToPublished,
    switchToDraft,
    dialog: unsavedSwitchDialog,
  } = useGuardedModeSwitch({ workflowName });

  const switchToPublished = requestSwitchToPublished;
  
  // Both discardDraft and discardSession share their implementation with
  // `WorkflowOverflowMenu` via this hook, which lives above the provider
  // in the tree. Keeping the logic in one place avoids the bug where the
  // menu used to call the bare store action and bypass the canvas/backend
  // sync that the provider performed.
  const { discardDraft, discardSession } = useDraftDiscardActions({ workflowName });
  
  // Save draft immediately (for running draft)
  const saveDraftNow = useCallback(async (): Promise<boolean> => {
    try {
      const result = await saveDraft();
      return result.success;
    } catch {
      return false;
    }
  }, [saveDraft]);
  
  // Memoize context value
  const value = useMemo<DraftModeContextValue>(() => ({
    // State
    isDraftMode,
    isReadOnly,
    areProdAndDraftEqual,
    draftStatus,

    // Autosave state
    isAutoSaving,
    lastSavedTime,
    saveError,
    needsSaved,

    // Publishing state
    isPublishing,

    // Actions
    switchToDraft,
    switchToPublished,
    discardDraft,
    discardSession,
    saveDraftNow,
  }), [
    isDraftMode,
    isReadOnly,
    areProdAndDraftEqual,
    draftStatus,
    isAutoSaving,
    lastSavedTime,
    saveError,
    needsSaved,
    isPublishing,
    switchToDraft,
    switchToPublished,
    discardDraft,
    discardSession,
    saveDraftNow,
  ]);
  
  return (
    <DraftModeContext.Provider value={value}>
      {children}
      {/*
        Unsaved-changes guard for switching draft → published. Mounted
        inside the provider so that callers using `switchToPublished` (e.g.
        `ModeToggle`) get the confirmation flow without rendering anything
        themselves. The hook owns its own dialog state.
      */}
      {unsavedSwitchDialog}
    </DraftModeContext.Provider>
  );
};

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to access draft mode context.
 * Must be used within a DraftModeProvider.
 */
export const useDraftMode = (): DraftModeContextValue => {
  const context = useContext(DraftModeContext);
  
  if (!context) {
    throw new Error('useDraftMode must be used within a DraftModeProvider');
  }
  
  return context;
};

/**
 * Hook to check if draft mode context is available.
 * Returns null if not within a provider (safe to use outside provider).
 */
export const useDraftModeOptional = (): DraftModeContextValue | null => {
  return useContext(DraftModeContext);
};
